'use client';

import { useEffect } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

import { recordPop, syncNavigation } from './navigationHistory';

/**
 * Invisible island that feeds {@link useBack}'s in-app depth (see
 * {@link navigationHistory}). Mounted ONCE at the app root so it observes every
 * route change and persists across them (the root layout never unmounts).
 *
 * `popstate` marks a browser back/forward (a pop); the `pathname`/`searchParams`
 * effect fires on every committed navigation (push / replace / pop, query-only
 * included) and classifies it by the `history.length` delta. Both signals are
 * universally supported, so this works on every browser — not just those with
 * the Navigation API.
 */
export function NavigationTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const onPopState = () => recordPop();
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    syncNavigation(window.history.length, pathname);
  }, [pathname, searchParams]);

  return null;
}
